import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  drawerStyle,
  stickyHeader,
  tableRowSize,
  defaultAllSelection,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { payrunCycleHeader } from "pages/PayRoll/PayRollHeaders";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { CreatePayrunCycle } from "./CreatePayrunCycle";
import { ViewPayrunCycle } from "./ViewPayRunCycle";
import moment from "moment";
import { useSaveView } from "components/CustomComponents/useSaveView";

export const PayrunCycle = ({ subModuleId }) => {
  const {
    domain,
    userScreenAccess,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payCycleRunDetails, setPayCycleRunDetails] = useState([]);
  const containerRef = useRef(null);
  const [payCycleDetails, setPayCycleDetails] = useState([]);
  // See PayRoll/Configuration/PayrollConfiguration/index.js — neither "Configuration" nor "Setup" ever
  // matched any real backend module_name; the correct one is "Settings".
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Settings",
    "Payrun Cycle"
  );

  const {
    result: payrunCycleData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}payroll/setup/payruncycle/pay-run-cycles/?company_ids=[${
      parseInt(topbarCompanyId) === defaultAllSelection ? "" : topbarCompanyId
    }]`,
  );
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [payrunViewedItem, setPayrunViewedItem] = useState(false);
  const [getId, setGetId] = useState("");

  const hanldeCreatePayrunCycle = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewPanel = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (payrunCycleData?.length !== 0 && payrunCycleData && payrunViewedItem) {
      const item = payrunCycleData.find((d) => d.id === getId);
      setPayCycleDetails(item);
    }
  }, [getId, payrunViewedItem, payrunCycleData]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setPayCycleRunDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const NewPayrunButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          hanldeCreatePayrunCycle();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create payrun cycle"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setPayrunViewedItem(false);
    }
  }, [reCallApi, topbarCompanyId]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "payrun_cycke_saved_views",
    fullHeaders: payrunCycleHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  return !payrunViewedItem && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Payrun Cycle
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define the payroll cycle, including its frequency, start date, and
            applicable employee group
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Payment Cycle Records"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={payrunCycleData}
            filteredData={payCycleRunDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={
              <>
                {newButtonComponent}
                {NewPayrunButton}
              </>
            }
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {visibleHeaders?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {payCycleRunDetails?.length === 0 ? (
                    <TableEmptyState dataLength={payrunCycleData} />
                  ) : (
                    payCycleRunDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell
                              key={head.key}
                              onClick={() => {
                                handleViewPanel();
                                setPayCycleDetails(value);
                              }}
                            >
                              {head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    statusId={
                                      value?.status === "Active"
                                        ? 1
                                        : value?.status === "Scheduled"
                                          ? 2
                                          : 3
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : head.key === "start_day" ? (
                                value?.pay_run_cycle_type === "Monthly" ? (
                                  (value?.date_of_month ?? "N/A")
                                ) : (
                                  (value?.start_day ?? "N/A")
                                )
                              ) : (
                                (value[head.key] ?? "N/A")
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {payCycleRunDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={payCycleRunDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <CreatePayrunCycle
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setPayrunViewedItem={setPayrunViewedItem}
          />
        ) : isViewPanelOpen ? (
          <ViewPayrunCycle
            onClose={handleViewClosePanel}
            payCycleDetails={payCycleDetails}
            reCallApi={reCallApi}
            setGetId={setGetId}
            setPayrunViewedItem={setPayrunViewedItem}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
